import React, {memo} from 'react';
import PropTypes from 'prop-types';
import './MferHead.css';
import CONSTANTS from './constants';

export function isDeadPiece(type) {
    return !isAlivePiece(type);
}
export function isNormalPiece(type) {
    return type >= 1 && type <= 6;
}
export function isPowerUp(type) {
    return type === 7; // For now only super power up, but can be expanded
}
export function isSuperPowerUp(type) {
    return type === 7;
}
export function isBarrelTile(type) {
    return type === 11 || type === 12 || type === 13;
}
export function isBarrelFull(type) {
    return type === 11;
}
export function isBarrelHalf(type) {
    return type === 12;
}
export function isBarrelEmpty(type) {
    return type === 13;
}
export function damageBarrel(barrelType) {
    if (barrelType === 11) return 12; // Full -> Half
    if (barrelType === 12) return 13; // Half -> Empty
    return barrelType; // Already empty or not a barrel
}
export function isAlivePiece(type) {
    return isNormalPiece(type) || isSuperPowerUp(type) || (type === 11 || type === 12); // Only full and half barrels are alive
}
export function turnAlivePieceDead(aliveType) {
    // assume its alive
    return aliveType + CONSTANTS.N_DEAD_ZONE;
}
function areEqual(prevProps, nextProps) {

    const prevIsSelected = prevProps.selectedJewel !== null
    ? (prevProps.selectedJewel.colIndex === prevProps.y && prevProps.selectedJewel.rowIndex === prevProps.x)
    : false;

    const nextIsSelected = nextProps.selectedJewel !== null
        ? (nextProps.selectedJewel.colIndex === nextProps.y && nextProps.selectedJewel.rowIndex === nextProps.x)
        : false;

    const same = prevProps.type === nextProps.type &&
                 prevIsSelected === nextIsSelected &&
                 prevProps.showCoordinates === nextProps.showCoordinates &&
                 prevProps.previewSwap === nextProps.previewSwap;
    return same;

}

export const MferHead = memo(function MferHead({ type, x, y, selectedJewel, shouldBeCleared, onClick, imageMap, levelID, showCoordinates, previewSwap}) {
    const isSelected=(selectedJewel && selectedJewel.colIndex === y && selectedJewel.rowIndex === x);

    // Helper to get direction arrow emoji
    const getDirectionArrow = (direction) => {
        switch(direction) {
            case 'up': return '↑';
            case 'down': return '↓';
            case 'left': return '←';
            case 'right': return '→';
            default: return '';
        }
    };

    const getImageSrc = () => {
        // Handle barrel tiles - use CSS styling instead of images for now
        if (type === 11 || type === 12 || type === 13) {
            return null; // Will be handled by CSS
        }
        // Handle power-ups
        else if (type === 7) {
            if (levelID === "nakamigo_crush") {
                return '/images/mfersbeheaded/naka/nakaking-nobg.png';
            } else if (levelID === "tiny_dino" || levelID === "tiny_dinos_automated") {
                return '/images/mfersbeheaded/tinydinos/charizard.png';
            }
            return '/images/mfersbeheaded/heads/7235.png';
        } else if (type === 1007) {
            if (levelID === "nakamigo_crush") {
                return '/images/mfersbeheaded/naka/nakaking-nobg.png';
            } else if (levelID === "tiny_dino" || levelID === "tiny_dinos_automated") {
                return '/images/mfersbeheaded/tinydinos/charizard.png';
            }
            return '/images/mfersbeheaded/heads/7235.png';
        } else {
            return imageMap[type];
        }
    };

    const imageSrc = getImageSrc();
    
    // Handle rock tiles (EtherRock) with images
    if (type === 11 || type === 12 || type === 13) {
        const getRockImageSrc = () => {
            if (type === 11) return '/images/mfersbeheaded/etherrock.svg'; // Full rock
            if (type === 12) return '/images/mfersbeheaded/etherrock-cracked.svg'; // Cracked rock
            return null; // Empty rock uses CSS explosion animation
        };

        const rockImageSrc = getRockImageSrc();

        if (type === 13) {
            // Empty rock - show explosion animation
            return (
                <div
                    className={`img-container rock-container ${isSelected ? 'selected' : ''} ${previewSwap ? 'preview-swap' : ''}`}
                    id={`cell-${x}-${y}`}
                    data-testid={`cell-${x}-${y}`}
                    data-row={x}
                    data-col={y}
                    data-type={type}
                    data-selected={isSelected ? 'true' : 'false'}
                >
                    <div
                        className={`mfer-head rock-tile rock-${type} level-${levelID} ${isSelected ? 'selected' : ''} ${shouldBeCleared ? 'to-clear' : ''}`}
                        onClick={onClick}
                        data-selected={isSelected ? 'true' : 'false'}
                    >
                        💥
                    </div>
                    {previewSwap && previewSwap.isFrom && (
                        <div className="preview-direction-overlay">
                            {getDirectionArrow(previewSwap.direction)}
                        </div>
                    )}
                </div>
            );
        } else {
            // Full or cracked rock - show SVG image
            return (
                <div
                    className={`img-container rock-container ${isSelected ? 'selected' : ''} ${previewSwap ? 'preview-swap' : ''}`}
                    id={`cell-${x}-${y}`}
                    data-testid={`cell-${x}-${y}`}
                    data-row={x}
                    data-col={y}
                    data-type={type}
                    data-selected={isSelected ? 'true' : 'false'}
                >
                    <img
                        src={rockImageSrc}
                        alt={`EtherRock ${type === 11 ? 'Full' : 'Cracked'}`}
                        className={`mfer-head rock-tile rock-${type} level-${levelID} ${isSelected ? 'selected' : ''} ${shouldBeCleared ? 'to-clear' : ''}`}
                        onClick={onClick}
                        data-selected={isSelected ? 'true' : 'false'}
                    />
                    {showCoordinates && (
                        <div className="coordinate-overlay">
                            ({x},{y},{type})
                        </div>
                    )}
                    {previewSwap && previewSwap.isFrom && (
                        <div className="preview-direction-overlay">
                            {getDirectionArrow(previewSwap.direction)}
                        </div>
                    )}
                </div>
            );
        }
    }
    
    return (
        <div
            className={`img-container ${isSelected ? 'selected' : ''} ${previewSwap ? 'preview-swap' : ''}`}
            id={`cell-${x}-${y}`}
            data-testid={`cell-${x}-${y}`}
            data-row={x}
            data-col={y}
            data-type={type}
            data-selected={isSelected}
        >
            <img
                src={imageSrc}
                alt={`Jewel type ${type}`}
                className={`mfer-head mfer-head-${type} level-${levelID} ${isSelected ? 'selected' : ''} ${shouldBeCleared ? 'to-clear' : ''}`}
                onClick={onClick}
                style={{ left: `${x * 50}px`, top: `${y * 50}px` }}
                data-selected={isSelected ? 'true' : 'false'}
            />
            {showCoordinates && (
                <div className="coordinate-overlay">
                    ({x},{y},{type})
                </div>
            )}
            {previewSwap && previewSwap.isFrom && (
                <div className="preview-direction-overlay">
                    {getDirectionArrow(previewSwap.direction)}
                </div>
            )}
        </div>
    );
}, areEqual);

MferHead.propTypes = {
    type: PropTypes.number.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    selectedJewel: PropTypes.shape({
        rowIndex: PropTypes.number.isRequired,
        colIndex: PropTypes.number.isRequired
    }),
    shouldBeCleared: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    imageMap: PropTypes.object,
    levelID: PropTypes.string.isRequired,
    showCoordinates: PropTypes.bool,
    previewSwap: PropTypes.shape({
        isFrom: PropTypes.bool,
        direction: PropTypes.oneOf(['up', 'down', 'left', 'right'])
    })
};

MferHead.defaultProps = {
    selectedJewel: null,
    shouldBeCleared: false,
    imageMap: null,
    showCoordinates: false,
    previewSwap: null
};

export default MferHead;
